import { HelmetProvider } from "react-helmet-async";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import React, { useEffect, Suspense } from "react";
import Footer from "./components/common/Footer";
import Header from "./components/common/Header";
import Sidebar from "./components/common/Sidebar";
import SplashScreen from "./components/common/SplashScreen";
import FeatureAnnouncement from "./components/common/FeatureAnnouncement";
import splashConfig from "./config/splashConfig";
import { AuthProvider, useAuth } from "./features/auth/AuthContext";
import { SeasonProvider, useSeasonContext } from "./context/SeasonContext";
import { setLogoutFunction, setCurrentSeasonGetter } from "./utils/api";
import "./styles/main.css";

// Eagerly loaded — tiny, needed immediately on every route
import ProtectedRoute from "./features/auth/ProtectedRoute";

// Lazily loaded — each becomes its own JS chunk
const ConfirmSignUpPage = React.lazy(
  () => import("./features/auth/ConfirmSignUpPage"),
);
const ForgotPasswordPage = React.lazy(
  () => import("./features/auth/ForgotPasswordPage"),
);
const LoginPage = React.lazy(() => import("./features/auth/LoginPage"));
const RegisterPage = React.lazy(() => import("./features/auth/RegisterPage"));
const AccountPage = React.lazy(
  () => import("./features/auth/account/AccountPage"),
);
const Dashboard = React.lazy(
  () => import("./features/dashboard/DashboardPage"),
);
const HomePage = React.lazy(() => import("./features/home/HomePage"));
const HowItWorksPage = React.lazy(
  () => import("./features/howItWorks/HowItWorksPage"),
);
const LeaderboardPage = React.lazy(
  () => import("./features/leaderboard/LeaderboardPage"),
);
const ParallelUniversePage = React.lazy(
  () => import("./features/parallelUniverse/ParallelUniversePage"),
);
const PredictionsPage = React.lazy(
  () => import("./features/predictions/PredictionsPage"),
);
const ProfilePage = React.lazy(() => import("./features/profile/ProfilePage"));
const ResultsPage = React.lazy(() => import("./features/results/ResultsPage"));
const RollOfHonourPage = React.lazy(
  () => import("./features/rollOfHonour/RollOfHonourPage"),
);
const TeamBreakdownPage = React.lazy(
  () => import("./features/teamBreakdown/TeamBreakdownPage"),
);
const AdminResultsPage = React.lazy(
  () => import("./features/admin/results/ResultsPage"),
);
const AdminSeasonsPage = React.lazy(
  () => import("./features/admin/seasons/SeasonsPage"),
);
const AdminLastManStandingPage = React.lazy(
  () => import("./features/admin/lastManStanding/AdminLastManStandingPage"),
);
const LastManStandingPage = React.lazy(
  () => import("./features/lastManStanding/LastManStandingPage"),
);
const LastManStandingHistoryPage = React.lazy(
  () => import("./features/lastManStanding/LastManStandingHistoryPage"),
);
const LastManStandingHowItWorksPage = React.lazy(
  () => import("./features/lastManStanding/LastManStandingHowItWorksPage"),
);

// Component to connect SeasonContext to API interceptor
function SeasonAPIConnector({ children }) {
  const { currentSeason } = useSeasonContext();

  useEffect(() => {
    setCurrentSeasonGetter(() => currentSeason);
  }, [currentSeason]);

  return children;
}

function AppContent() {
  const { loading, logout, isAuthenticated, isAdmin, hasProfile, user } =
    useAuth();
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(false);

  useEffect(() => {
    setLogoutFunction(logout);
  }, [logout]);

  const toggleSidebar = () => setIsSidebarOpen((prev) => !prev);
  const closeSidebar = () => setIsSidebarOpen(false);

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <Router
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <div
        className={splashConfig.enabled ? "App App--splash-active" : "App"}
        aria-hidden={splashConfig.enabled ? "true" : undefined}
      >
        <Header isSidebarOpen={isSidebarOpen} onToggleSidebar={toggleSidebar} />
        <div className="app-body">
          {isAuthenticated && (
            <Sidebar
              isOpen={isSidebarOpen}
              onClose={closeSidebar}
              isAdmin={isAdmin}
              hasProfile={hasProfile}
            />
          )}
          <main className="app-main">
            <div className="container">
              <Suspense fallback={<div className="loading-spinner" />}>
                {/* [F12] key={currentSeason} removed — was causing full router
                    remount on season change, destroying all page state and
                    re-triggering all data fetches. Season changes propagate
                    through SeasonContext instead; each page responds via its
                    own useEffect. */}
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/register" element={<RegisterPage />} />
                  <Route path="/confirm" element={<ConfirmSignUpPage />} />
                  <Route
                    path="/forgot-password"
                    element={<ForgotPasswordPage />}
                  />
                  <Route
                    path="/dashboard"
                    element={
                      <ProtectedRoute>
                        <Dashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/predictions"
                    element={
                      <ProtectedRoute>
                        <PredictionsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/leaderboard"
                    element={
                      <ProtectedRoute>
                        <LeaderboardPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/roll-of-honour"
                    element={
                      <ProtectedRoute>
                        <RollOfHonourPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/parallel-universe"
                    element={
                      <ProtectedRoute>
                        <ParallelUniversePage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/team-breakdown"
                    element={
                      <ProtectedRoute>
                        <TeamBreakdownPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/results"
                    element={
                      <ProtectedRoute>
                        <AdminResultsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/seasons"
                    element={
                      <ProtectedRoute>
                        <AdminSeasonsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/last-man-standing"
                    element={
                      <ProtectedRoute>
                        <AdminLastManStandingPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/last-man-standing"
                    element={
                      <ProtectedRoute>
                        <LastManStandingPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/last-man-standing/history"
                    element={
                      <ProtectedRoute>
                        <LastManStandingHistoryPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/last-man-standing/how-it-works"
                    element={<LastManStandingHowItWorksPage />}
                  />
                  <Route
                    path="/profile"
                    element={
                      <ProtectedRoute>
                        <ProfilePage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/account"
                    element={
                      <ProtectedRoute>
                        <AccountPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/how-it-works" element={<HowItWorksPage />} />
                  <Route
                    path="/results"
                    element={
                      <ProtectedRoute>
                        <ResultsPage />
                      </ProtectedRoute>
                    }
                  />
                </Routes>
              </Suspense>
            </div>
          </main>
        </div>
        <Footer />
      </div>
      {splashConfig.enabled && <SplashScreen />}
      {!splashConfig.enabled && isAuthenticated && hasProfile && (
        <FeatureAnnouncement userId={user?.sub} />
      )}
    </Router>
  );
}

function AppWithSeason() {
  const { isAuthenticated } = useAuth();
  return (
    <SeasonProvider isAuthenticated={isAuthenticated}>
      <SeasonAPIConnector>
        <AppContent />
      </SeasonAPIConnector>
    </SeasonProvider>
  );
}

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <AppWithSeason />
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
